import type { EditorProps, EditorView } from '@tiptap/pm/view';

export const findNearestNodePosition = (
  view: EditorView,
  coords: { left: number; top: number }
): number | null => {
  // Get the document position at the given coordinates
  const posAtCoords = view.posAtCoords(coords);

  if (!posAtCoords) {
    console.error('Invalid coordinates or outside editor bounds.');
    return null;
  }

  const { pos } = posAtCoords; // Document position

  // Get the node at the position
  const resolvedPos = view.state.doc.resolve(pos);
  const node = resolvedPos.node();

  // If we are directly on a node boundary
  if (node) {
    return pos; // Return the position if a node is found
  }

  // Move forward to find the nearest node after the resolved position
  let nearestNodePos = pos;

  // Iterate over the siblings after the current position
  const parentNode = resolvedPos.node(resolvedPos.depth);
  if (parentNode) {
    // Check if the position is at the end of the parent node
    if (pos >= parentNode.nodeSize + resolvedPos.start(resolvedPos.depth)) {
      // If at the end, move to the next sibling (if any)
      nearestNodePos = resolvedPos.after(resolvedPos.depth);
    } else {
      // Otherwise, look for the next node within the same parent
      const nextChildIndex = resolvedPos.indexAfter(resolvedPos.depth);
      const nextNode = parentNode.child(nextChildIndex);
      if (nextNode) {
        nearestNodePos =
          resolvedPos.start(resolvedPos.depth) + nextNode.nodeSize;
      } else {
        // If no next node, go to the end of the parent
        nearestNodePos = resolvedPos.after(resolvedPos.depth);
      }
    }
  }

  return nearestNodePos;
};

export const editorHandleDrop =
  (
    handleFileUpload: ((file: File) => Promise<string | null>) | undefined
  ): EditorProps['handleDrop'] =>
  (view, event, _slice, moved) => {
    if (!handleFileUpload) return;

    const file = event.dataTransfer?.files?.[0];

    if (moved || !file) return;

    event.preventDefault();
    event.stopPropagation();

    const { schema, tr } = view.state;

    const nodePosition = findNearestNodePosition(view, {
      left: event.clientX,
      top: event.clientY,
    });

    if (!nodePosition) return;

    const imageUploadProgressNode = schema.nodes.imageUploadProgress.create();
    tr.insert(nodePosition, imageUploadProgressNode);
    view.dispatch(tr);

    handleFileUpload(file);

    return true;
  };
